import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Link from 'next/link';
import { getProductsFromCommonCarousel } from 'aster-foundation/core/epharmacy/Product/redux/action';
import { MY_ASTER_CATEGORY } from '@common/Constants/analytics';
import useIntersectionObserver from '@common/Hooks/useIntersectionObserver';
import { getProductDetailAttributes } from '@common/Utility/insider-object-integration';
import { errorHandling, getAttributeItemFromArray, getReferralPageForGA } from '@common/Utility/common-utils';
import { AE_GLOBAL, MY_CART_ROUTE, ONE_ASTER_CATEGORY_HOME, ONLINE_PHARMACY_ROUTE } from '@common/Utility/constants';
import { sendEventToSmartLook } from '@common/Utility/smartlook';
import logger from '@common/Utility/logger';
import { dataLayerSendEvent, getProductItemObject } from 'Epharmacy/EpharmacyAnalytics';
import fetchRECarouselData from 'Epharmacy/RecommendationEngine/DynamicREAPICall';
import ProductItem from '../ProductItem/ProductItem';
import SectionBox from '../SectionBox/SectionBox';
import SlickSlider from '../SlickSlider/SlickSlider';
import { CommonProductCarouselStyled } from './CommonProductCarouselStyled';
import WithLoader from '../Loader/WithLoader';
import { useNavigationURL } from '@common/Utility/generateNavigationURL';
import { useTranslation } from '@common/i18n';

export const responsive = [
  {
    breakpoint: 980,
    settings: {
      slidesToShow: 5,
    },
  },
  {
    breakpoint: 768,
    settings: {
      slidesToShow: 3,
    },
  },
  {
    breakpoint: 480,
    settings: {
      slidesToShow: 1,
      variableWidth: true,
      infinite: true,
    },
  },
];

const CommonProductCarousel = (props) => {
  const {
    title,
    carouselTypes,
    data,
    brandId,
    setLoading,
    referrerPage,
    viewAllLink,
    isLandingPage,
    sourceType,
    showViewAllLink,
    linkWebViewAll,
    viewAll,
    type,
    screenType,
    responsiveProduct,
    pageType,
    sectionTitle,
    bgColor,
    noOfElementInRow,
    isPdpQuickView,
    emptySphValues = () => {}
  } = props;

  const dispatch = useDispatch();
  const router = useRouter();
  const generateURL = useNavigationURL();
  const t = useTranslation();
  const offerType = brandId;
  const [viewAllByCategory, setViewAllByCategory] = React.useState('');
  const [viewAllType, setViewAllType] = React.useState('');
  const zoneData = useSelector((state) => state?.app?.userLocationReducer?.zone);
  const loading = useSelector((state) => state?.epharmacy?.getCommonProductCarouselBySearch?.isFetching);
  const guestLogin = useSelector((state) => state?.astercore?.guestAccessToken?.data);
  const userLogin = useSelector((state) => state?.astercore?.checkUserExist?.data?.entry[0]?.resource);

  const [productCarouselData, setProductCarouselData] = useState(null);
  const [showViewAll, setViewAll] = useState(false);
  const [isMyCartPage, setIsMyCartPage] = useState(false);
  const zoneDataMemo = React.useMemo(() => zoneData, [zoneData]);
  const screenName = router?.pathname;
  const bannerViewEventFired = useRef();

  const carouselTitle = useMemo(() => {
    if (title && title !== '') {
      return title;
    }
    if (productCarouselData?.title && productCarouselData?.title !== '') {
      return productCarouselData?.title;
    }
    if (carouselTypes) {
      return carouselTypes;
    }
    return '';
  }, [title, productCarouselData?.title, carouselTypes]);

  useEffect(() => {
    if (router?.pathname?.indexOf(MY_CART_ROUTE) > -1) {
      setIsMyCartPage(true);
    }
  }, [router?.pathname]);

  const viewAllLinkClickEvent = () => {
    dataLayerSendEvent('view_all_link_click', {
      screen_name: router?.query?.slugname
        ? router?.query?.slugname
        : props?.pageName === ONE_ASTER_CATEGORY_HOME?.PHARMACY
        ? 'epharmacy homepage'
        : props?.pageName === ONE_ASTER_CATEGORY_HOME?.CORE
        ? 'asco homepage'
        : props?.pageName === ONE_ASTER_CATEGORY_HOME?.DOCTOR
        ? 'doctor consult homepage'
        : props?.pageName,
      page_type: props?.pageType,
      page_id: null,
      myaster_category: 'epharmacy',
      section_name: props?.title,
      type: 'product carousel',
    });
    sendEventToSmartLook('view_all_link_click');
  };

  const rightTopSection = () => {
    if (productCarouselData?.data.length > 5 && (showViewAll || props?.showViewAllLink)) {
      if (viewAllType === 'brand' && viewAll) {
        return (
          <Link href={generateURL(viewAllByCategory)} passHref>
            <a className="link me-2" tabIndex="0" role="button" aria-pressed="false" onClick={() => viewAllLinkClickEvent()}>
              {t('EPharmacyHome.viewAll')}
            </a>
          </Link>
        );
      }
      if (viewAllType === 'category' && viewAll) {
        return (
          <Link href={generateURL(viewAllByCategory)} passHref>
            <a className="link me-2" tabIndex="0" role="button" aria-pressed="false" onClick={() => viewAllLinkClickEvent()}>
              {t('EPharmacyHome.viewAll')}
            </a>
          </Link>
        );
      }
      if ((productCarouselData?.showViewAll || showViewAllLink) && viewAllLink) {
        return (
          <Link
            href={generateURL(viewAllLink)}
            passHref
          >
            <a
              className="link me-2"
              tabIndex="0"
              role="button"
              aria-pressed="false"
              onClick={() => {
                dataLayerSendEvent('category_view_all', {
                  section_name: props?.title,
                });
                router.push(generateURL(viewAllLink));
                viewAllLinkClickEvent();
              }}
            >
              {t('EPharmacyHome.viewAll')}
            </a>
          </Link>
        );
      }
      if (isLandingPage === true && viewAllLink) {
        return (
          <Link href={generateURL(viewAllLink)} passHref>
            <a
              className="link me-2"
              tabIndex="0"
              role="button"
              aria-pressed="false"
              href={generateURL(viewAllLink)}
              onClick={() => {
                router.push(generateURL(viewAllLink));
              }}
            >
              {t('EPharmacyHome.viewAll')}
            </a>
          </Link>
        );
      }
    }
    return '';
  };

  const getCommonCarouselProducts = (brand, type) => {
    if (brand && brand !== '' && zoneData?.length > 0) {
      dispatch(
        getProductsFromCommonCarousel({
          params: { id: brand, type, pageSize: 10, pageFrom: 0, zone: zoneData?.join(',') },
          onSuccess: (res) => {
            setProductCarouselData(res);
            setViewAll(res?.showViewAll);
            if ((type === 'category' || res?.type === 'category') && linkWebViewAll!="" && linkWebViewAll!=null ) {
              if (linkWebViewAll && viewAll){
                setViewAllByCategory(
                  `${ONLINE_PHARMACY_ROUTE}/${linkWebViewAll}`
                  );
              }
            }
            if ((type === 'brand' || res?.type === 'brand') && linkWebViewAll!="" && linkWebViewAll!=null) {
              if (linkWebViewAll && viewAll){
                setViewAllByCategory(
                  `${ONLINE_PHARMACY_ROUTE}/${linkWebViewAll}`
                  );
              }
            }
            if (res?.type === 'offerType' && linkWebViewAll!="" && linkWebViewAll!=null) {
              if (linkWebViewAll && viewAll){
                setViewAllByCategory(
                  `${ONLINE_PHARMACY_ROUTE}/${linkWebViewAll}`
                  );
              }
          }
          },
          onFailure: (err) => {
            errorHandling(err, true, screenType || screenName, pageType);
          },
        }),
      );
    }
  };

  useEffect(() => {
    if (offerType) {
      if (sourceType === 'category ID') {
        setViewAllType('category');
        getCommonCarouselProducts(brandId, 'category');
      } else if (sourceType === 'Brand ID') {
        setViewAllType('brand');
        getCommonCarouselProducts(brandId, 'brand');
      } else if (data) {
        setProductCarouselData(data?.productList);
      } else if (zoneData && zoneData?.length > 0) {
        fetchRECarouselData(
          dispatch,
          { guestLogin, userLogin, zoneData },
          {
            offerType,
            onSuccess: (response) => {
              setProductCarouselData(response);
            },
            onFailure: (err) => {
              errorHandling(err, true, screenName, pageType);
              logger.error('Error on pharmacy home page product carousel', err);
            },
          },
        );
      }
    }
    else {
      setProductCarouselData(data?.productList);
    }
  }, [zoneDataMemo, data]);

  useEffect(() => {
    setLoading(loading);
  }, [loading]);

  const prevSectionNameRef = useRef(null);
  
  const getSlickSlider = () => {
    let slickSlider = {};
    if (screenType === 'appointment detail') {
      slickSlider = {
        responsive: responsiveProduct,
        slidesToShow: 3.6,
        infinite: false,
        className: 'productSlider',
        dots: false,
      };
    } else if (screenType === 'booking confirmation') {
      slickSlider = {
        responsive: responsiveProduct,
        slidesToShow: 4.6,
        infinite: false,
        className: '',
        dots: false,
      };
    } else {
      slickSlider = {
        responsive: responsive,
        slidesToShow: noOfElementInRow ?? 5,
        infinite: false,
        className: '',
        dots: false,
      };
    }
    return slickSlider;
  };

  const getPageName = (type) => {
    let pageType;
    if(router?.asPath?.indexOf('/p') > 0) {
      pageType = type;
    } else {
      getReferralPageForGA(router?.asPath);
    }

    return pageType;
  };

  const handleOnView = () => {
    if (!bannerViewEventFired.current && productCarouselData?.data && productCarouselData?.data.length > 0) {
      bannerViewEventFired.current = true;
      const serializedAppointment = JSON.parse(sessionStorage.getItem('serializedAppointment'));
      const currentSectionName = carouselTitle || sectionTitle;

      if (currentSectionName !== prevSectionNameRef.current) {
        dataLayerSendEvent('view_item_list', {
          section_name: currentSectionName,
          referrer_page: referrerPage,
          myaster_category: MY_ASTER_CATEGORY.AE,
          ...(screenType ? { screen_name: screenType } : {}),
          ...(pageType ? { page_type: pageType } : {}),
          ...(serializedAppointment?.doctorSpecialty
            ? { speciality: serializedAppointment?.doctorSpecialty?.toLowerCase() }
            : {}),
          ...(serializedAppointment?.consultationType
            ? { appointment_mode: serializedAppointment?.consultationType?.toLowerCase() }
            : {}),
          ...(serializedAppointment?.doctorName
            ? { doctor_name: serializedAppointment?.doctorName?.toLowerCase() }
            : {}),
          ...(serializedAppointment?.facilityName
            ? { facility_name: serializedAppointment?.facilityName?.toLowerCase() }
            : {}),
          ecommerce: {
            items: productCarouselData?.data.map((element, index) => ({
              ...getProductItemObject(element),
              index,
              ...getProductDetailAttributes(element, 'Pharmacy'),
            })),
          },
        });

        const properties = {
          type: offerType,
        };
        sendEventToSmartLook('view_item_list', properties);

        prevSectionNameRef.current = currentSectionName;
      }
    }
  };

  const { inViewRef } = useIntersectionObserver(handleOnView, { threshold: 0.1 });

  const getScreenName = (item) => {
    if (screenType) return screenType;

    if (type === ONE_ASTER_CATEGORY_HOME.CORE) return 'asco homepage';

    if (type === ONE_ASTER_CATEGORY_HOME.PHARMACY) return 'epharmacy homepage';

    return getPageName(item?.name);
  };

  return productCarouselData?.data && productCarouselData?.data.length > 0 ? (
    <CommonProductCarouselStyled ref={inViewRef} title={carouselTitle} viewAll={props?.viewAll} bgColor={bgColor}>
      <SectionBox
        title={carouselTitle}
        customClass="sectionBox ePharmacy"
        customOuterClass="pb-0 whiteBg"
        rightTop={rightTopSection()}
        isGlobalCarousel={props?.isGlobalCarousel}
        bgColor={bgColor}
        >
        <SlickSlider sliderSettings={getSlickSlider()}>
          {productCarouselData?.data &&
            productCarouselData?.data.map((item, index) => {
              const smallImage = item.small_image;
              return (
                <ProductItem
                  key={item?.productId}
                  index={index}
                  avgRating={item?.avgRating}
                  badgeDetails={item?.badgeDetails}
                  isOptics={item?.isOpticalProduct}
                  offerBadge={item?.badgeDetails?.offerBadge}
                  asterBadge={item?.badgeDetails?.asterBadge?.[0]}
                  categoryImage={smallImage || getAttributeItemFromArray(item?.customAttribute, 'small_image')}
                  name={item?.name}
                  newPrice={item?.specialPrice || item?.special_price}
                  actualPrice={item?.price}
                  express={item?.express || item?.express_bool}
                  isFlash={item?.isFlash}
                  deliverBy={item?.deliverBy}
                  deliveryText={item?.deliveryText}
                  currency={item?.currency}
                  freeDelivery={item?.freeDelivery}
                  outOfStock={!item?.inStock}
                  sku={item?.sku}
                  availableStock={item?.quantity}
                  product={item}
                  orderWithin={item?.orderWithin}
                  sourceContainerTitle={carouselTitle}
                  isLoader={false}
                  pageName={getScreenName(item)}
                  sectionName={carouselTitle || sectionTitle}
                  itemListName={carouselTitle}
                  itemListId={item?.offerType}
                  screen={getScreenName(item)}
                  pageType={
                    pageType ||
                    (type === ONE_ASTER_CATEGORY_HOME.CORE || type === ONE_ASTER_CATEGORY_HOME.PHARMACY
                      ? 'homepage'
                      : getReferralPageForGA(router?.asPath))
                  }
                  isMyCartPage={isMyCartPage}
                  isGlobalCarousel={props?.isGlobalCarousel}
                  isBundle={item?.isBundled}
                  isScheduled={item?.isScheduled}
                  BundleDetails={item?.tierPrices}
                  BundleImage={item?.small_image}
                  referrerPage={referrerPage}
                  hasRX={item?.itemLabels?.includes(AE_GLOBAL?.RX)}
                  facetResponse={item}
                  isPdpQuickView={isPdpQuickView}
                  shouldNotShowBundle={isPdpQuickView}
                  emptySphValues={emptySphValues}
                />
              );
            })}
        </SlickSlider>
      </SectionBox>
    </CommonProductCarouselStyled>
  ) : (
    ''
  );
};

CommonProductCarousel.propTypes = {
  data: PropTypes.object,
  title: PropTypes.string,
  viewAllLink: PropTypes.string,
  isLandingPage: PropTypes.bool,
  referrerPage: PropTypes.string,
  brandId: PropTypes.string,
  carouselTypes: PropTypes.string,
  setLoading: PropTypes.func,
  sourceType: PropTypes.string,
  showViewAllLink: PropTypes.bool,
  isGlobalCarousel: PropTypes.bool,
  isViewAll: PropTypes.bool,
  isTitle: PropTypes.bool,
  screenType: PropTypes.string,
  responsiveProduct: PropTypes.array,
  pageType: PropTypes.string,
  sectionTitle: PropTypes.string,
};

CommonProductCarousel.defaultProps = {
  isGlobalCarousel: false,
};

export default WithLoader(CommonProductCarousel);
